import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import BookingPage from '../../pages/BookingPage/BookingPage';
import { PublicBookingPageResponse } from '../../services/publicBookingApi';
import { 
  BookingAdvanceUnit,
  CustomerContactPreference, 
  DepositType, 
  PlanFeature,
} from '../../dataModels/models';
import BrandingSlider from './BrandingSlider';
import { BRANDING_VARIANTS } from './BrandingVariations';
import { useScreenSize } from '../../contexts/ScreenSizeContext';
import { resolveColorScheme } from '../../styling/ColorSchemes';
import { navigateToSignupWithTracking } from '../../services/trackingNavigation';

// Realistic demo data
const DEMO_TEST_DATA: PublicBookingPageResponse = {
  bookingSlug: 'lovis-nails-demo',
  business: {
    name: "Lovi's Nails",
    timezone: 'America/Chicago',
    contact: {
      email: 'nails@lovibook.com',
      phone: '+1 (555) 624-5700',
      address: '125 Blossom Lane, Austin, TX 78701',
    },
    logoUrl: null,
    plan: {
      planId: 'plan_standard',
      features: [PlanFeature.EMAIL_NOTIFICATIONS],
    },
    notificationSettings: {
      customerContactPreference: CustomerContactPreference.EMAIL,
      customerContactEmail: 'nails@lovibook.com',
      customerContactPhone: null,
    },
    bookingSettings: {
      advanceBookingValue: 30,
      advanceBookingUnit: BookingAdvanceUnit.DAYS,
      appointmentInstructions: 'Please arrive with bare nails when possible. Inspiration photos are always welcome!',
    },
  },
  provider: {
    id: 'provider-1',
    name: 'Lovi',
  },
  services: [
    {
      id: 'service-1',
      name: 'Express Manicure',
      description: 'Nail shaping, cuticle care, and your choice of classic polish',
      durationMinutes: 30,
      price: 28,
      currency: 'USD',
    },
    {
      id: 'service-2',
      name: 'Gel Manicure',
      description: 'Long-lasting gel color with detailed nail and cuticle care',
      durationMinutes: 60,
      price: 48,
      currency: 'USD',
    },
    {
      id: 'service-3',
      name: 'Custom Nail Art Set',
      description: 'A full custom set with hand-painted details inspired by your style',
      durationMinutes: 90,
      price: 78,
      currency: 'USD',
    },
  ],
  branding: null,
  paymentSummary: {
    depositRequired: false,
    paymentReady: false,
    depositType: DepositType.FLAT,
    depositValue: 0,
    isRefundable: false,
    refundWindowHours: 0,
  },
};

// Generate mock availability data for the next 30 days
const generateDemoAvailability = (): Record<string, Record<string, string[]>> => {
  const availability: Record<string, Record<string, string[]>> = {};
  const today = new Date();

  for (let i = 0; i < 30; i++) {
    const date = new Date(today);
    date.setDate(date.getDate() + i);

    // Lovi's Nails is closed Sunday and Monday.
    if (date.getDay() === 0 || date.getDay() === 1) continue;

    const dateStr = date.toISOString().split('T')[0]; // YYYY-MM-DD

    // For each service, generate time slots
    for (const service of DEMO_TEST_DATA.services) {
      if (!availability[service.id]) {
        availability[service.id] = {};
      }

      // Generate 30-min or 60-min slots based on service duration
      const slots: string[] = [];
      const startHour = 10;
      const endHour = 18;

      for (let hour = startHour; hour < endHour; hour++) {
        for (let min = 0; min < 60; min += service.durationMinutes) {
          if (hour * 60 + min + service.durationMinutes <= endHour * 60) {
            slots.push(
              `${String(hour).padStart(2, '0')}:${String(min).padStart(2, '0')}:00`
            );
          }
        }
      }

      availability[service.id][dateStr] = slots;
    }
  }

  return availability;
};

/**
 * LandingPageDemo renders the BookingPage component with hardcoded demo data.
 * This replaces the iframe approach, providing instant loading and more flexibility.
 * Used on the landing page to showcase the booking experience to visitors.
 */
export default function LandingPageDemo() {
  const [selectedVariantId, setSelectedVariantId] = useState('pink');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const expandButtonRef = useRef<HTMLButtonElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const screen = useScreenSize();
  const colorScheme = resolveColorScheme('CLASSIC');
  const selectedVariant = BRANDING_VARIANTS.find(
    (v) => v.id === selectedVariantId
  ) ?? BRANDING_VARIANTS[0];

  useEffect(() => {
    if (!isModalOpen) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    const previouslyFocusedElement = document.activeElement as HTMLElement | null;
    const appRoot = document.getElementById('root');
    const rootWasInert = appRoot?.hasAttribute('inert') ?? false;
    const previousAriaHidden = appRoot?.getAttribute('aria-hidden');

    document.body.style.overflow = 'hidden';
    closeButtonRef.current?.focus();
    appRoot?.setAttribute('inert', '');
    appRoot?.setAttribute('aria-hidden', 'true');

    const handleModalKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsModalOpen(false);
        return;
      }
      if (event.key !== 'Tab') {
        return;
      }

      const modal = document.getElementById('landing-page-demo-modal');
      const focusableElements = Array.from(
        modal?.querySelectorAll<HTMLElement>(
          'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ) ?? [],
      );
      if (focusableElements.length === 0) {
        event.preventDefault();
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    document.addEventListener('keydown', handleModalKeyDown);
    return () => {
      document.removeEventListener('keydown', handleModalKeyDown);
      document.body.style.overflow = previousOverflow;
      if (!rootWasInert) {
        appRoot?.removeAttribute('inert');
      }
      if (typeof previousAriaHidden !== 'string') {
        appRoot?.removeAttribute('aria-hidden');
      } else {
        appRoot?.setAttribute('aria-hidden', previousAriaHidden);
      }
      previouslyFocusedElement?.focus();
    };
  }, [isModalOpen]);

  // Apply the selected branding to the demo business with colorSchemeName
  const demoDataWithBranding: PublicBookingPageResponse = {
    ...DEMO_TEST_DATA,
    branding: {
      colorSchemeName: selectedVariant.colorSchemeName,
      fontThemeName: null,
      logoUrl: null,
      headerStyle: null,
    },
  };

  const demoAvailability = generateDemoAvailability();

  return (
    <div
      style={{
        width: screen.isMobile ? 'calc(100% - 0.75rem)' : '100%',
        display: 'flex',
        flexDirection: 'column',
        margin: '0 auto',
        maxWidth: screen.isMobile ? '100%' : 'clamp(320px, 90vw, 720px)',
        gap: 0,
      }}
    >
      {/* Demo Container - ONLY for the preview */}
      <div
        style={{
          width: '100%',
          boxSizing: 'border-box',
          borderRadius: screen.isMobile ? '1rem' : '1.2rem',
          overflow: 'auto',
          overscrollBehavior: 'contain',
          maxHeight: '50vh',
          backgroundColor: '#ffffff',
          border: `1px solid ${colorScheme.BORDER}`,
          boxShadow: `0 16px 40px ${colorScheme.PRIMARY}12`,
          flexShrink: 0,
          position: 'relative',
        }}
      >
        {/* Expand Button */}
        <button
          ref={expandButtonRef}
          onClick={() => setIsModalOpen(true)}
          style={{
            position: 'absolute',
            top: '0.75rem',
            right: '0.75rem',
            zIndex: 10,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '2.5rem',
            height: '2.5rem',
            borderRadius: '0.5rem',
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            border: `1px solid ${colorScheme.BORDER}`,
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            backdropFilter: 'blur(10px)',
          }}
          title="Expand preview"
          aria-label="Expand booking preview"
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'rgba(255, 255, 255, 1)';
            (e.currentTarget as HTMLButtonElement).style.boxShadow = `0 4px 12px ${colorScheme.PRIMARY}20`;
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'rgba(255, 255, 255, 0.95)';
            (e.currentTarget as HTMLButtonElement).style.boxShadow = 'none';
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" />
          </svg>
        </button>

        <BookingPage
          previewMode={true}
          previewData={demoDataWithBranding}
          previewAvailabilityByServiceId={demoAvailability}
        />
      </div>

      {/* Branding Slider - COMPLETELY OUTSIDE */}
      <div style={{ marginTop: '0.3rem', display: 'flex', justifyContent: 'center', width: '100%' }}>
        <BrandingSlider
          selectedVariantId={selectedVariantId}
          onSelectVariant={setSelectedVariantId}
        />
      </div>

      {/* Expanded Preview Modal */}
      {isModalOpen && createPortal(
        <>
          {/* Backdrop */}
          <div
            onClick={() => setIsModalOpen(false)}
            style={{
              position: 'fixed',
              inset: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 1000,
              backdropFilter: 'blur(4px)',
            }}
          />

          {/* Modal Container */}
          <div
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 1001,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: screen.isMobile ? '0.75rem' : '2rem',
              boxSizing: 'border-box',
              overflow: 'hidden',
              pointerEvents: 'none',
            }}
          >
          {/* Modal Content */}
            <div
              id="landing-page-demo-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="landing-page-demo-modal-title"
              style={{
                flex: 1,
                width: '100%',
                height: '100%',
                maxWidth: '1120px',
                maxHeight: '900px',
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: '#ffffff',
                overflow: 'hidden',
                borderRadius: screen.isMobile ? '0.85rem' : '1.25rem',
                boxShadow: '0 24px 80px rgba(0, 0, 0, 0.28)',
                pointerEvents: 'auto',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header with Close Button */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '1rem 1.5rem',
                  borderBottom: `1px solid ${colorScheme.BORDER}`,
                  backgroundColor: '#fafafa',
                }}
              >
                <h3 id="landing-page-demo-modal-title" style={{ margin: 0, fontSize: '1rem', fontWeight: 600, color: colorScheme.TEXT_PRIMARY }}>
                  Experience the Booking Flow
                </h3>
                <button
                  ref={closeButtonRef}
                  onClick={() => setIsModalOpen(false)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '2rem',
                    height: '2rem',
                    borderRadius: '0.5rem',
                    backgroundColor: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    color: colorScheme.TEXT_MUTED,
                    transition: 'all 0.2s ease',
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'rgba(0, 0, 0, 0.05)';
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent';
                  }}
                  title="Close"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>

              {/* Booking Page Preview */}
              <div
                style={{
                  flex: 1,
                  overflow: 'auto',
                  overscrollBehavior: 'contain',
                  width: '100%',
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'center',
                }}
              >
                {/* Force BookingPage to expand to full modal width */}
                <div style={{ width: '100%', maxWidth: 'none' }}>
                  <BookingPage
                    previewMode={true}
                    previewData={demoDataWithBranding}
                    previewAvailabilityByServiceId={demoAvailability}
                  />
                </div>
              </div>

              {/* CTA Footer */}
              <div
                style={{
                  padding: screen.isMobile ? '1.2rem' : '1.6rem',
                  borderTop: `1px solid ${colorScheme.PRIMARY}30`,
                  background: `linear-gradient(135deg, ${colorScheme.PRIMARY}14 0%, ${colorScheme.ACCENT}24 100%)`,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.7rem',
                }}
              >
                <div style={{
                  color: colorScheme.TEXT_PRIMARY,
                  fontSize: screen.isMobile ? '0.78rem' : '0.86rem',
                  fontWeight: 800,
                  letterSpacing: '0.04em',
                  textAlign: 'center',
                }}>
                  Ready to simplify your bookings?
                </div>
                <button
                  onClick={() => {
                    setIsModalOpen(false);
                    navigateToSignupWithTracking();
                  }}
                  style={{
                    width: screen.isMobile ? '100%' : 'auto',
                    minWidth: screen.isMobile ? '0' : '17rem',
                    padding: screen.isMobile ? '1rem 1.4rem' : '1.05rem 2.75rem',
                    borderRadius: '999px',
                    background: `linear-gradient(135deg, ${colorScheme.PRIMARY} 0%, ${colorScheme.ACCENT} 100%)`,
                    color: '#ffffff',
                    border: '2px solid rgba(255, 255, 255, 0.75)',
                    outline: `2px solid ${colorScheme.PRIMARY}22`,
                    fontWeight: 900,
                    fontSize: screen.isMobile ? '1rem' : '1.08rem',
                    letterSpacing: '0.015em',
                    cursor: 'pointer',
                    boxShadow: `0 0.85rem 1.8rem ${colorScheme.PRIMARY}55, inset 0 1px 0 rgba(255, 255, 255, 0.3)`,
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.6rem',
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(-3px) scale(1.02)';
                    (e.currentTarget as HTMLButtonElement).style.boxShadow = `0 1.1rem 2.25rem ${colorScheme.PRIMARY}70, inset 0 1px 0 rgba(255, 255, 255, 0.35)`;
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(0) scale(1)';
                    (e.currentTarget as HTMLButtonElement).style.boxShadow = `0 0.85rem 1.8rem ${colorScheme.PRIMARY}55, inset 0 1px 0 rgba(255, 255, 255, 0.3)`;
                  }}
                >
                  <span>Create Yours Now!</span>
                  <span aria-hidden="true" style={{ fontSize: '1.25em', lineHeight: 1 }}>→</span>
                </button>
              </div>
            </div>
          </div>
        </>,
        document.body
      )}
    </div>
  );
}
